import { useEffect, useState, useSyncExternalStore } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { AlertTriangle, CloudOff, Loader2, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { kuyrukDurumu, kuyruguDinle, kuyruguGonder, reddedilenleriTemizle, reddedilenListesi } from "@/lib/cevrimdisi/kuyruk"
import { cn } from "@/lib/utils"

/** Tarayıcının bağlantı durumu (değişince yeniden çizilir). */
export function useCevrimici() {
  const [cevrimici, setCevrimici] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine))
  useEffect(() => {
    const ac = () => setCevrimici(true)
    const kapa = () => setCevrimici(false)
    window.addEventListener("online", ac)
    window.addEventListener("offline", kapa)
    return () => {
      window.removeEventListener("online", ac)
      window.removeEventListener("offline", kapa)
    }
  }, [])
  return cevrimici
}

export function useKuyrukDurumu() {
  return useSyncExternalStore(kuyruguDinle, kuyrukDurumu)
}

/**
 * ÇEVRİMDIŞI ROZETİ — her ekranın sol alt köşesinde, yalnız gerektiğinde:
 * bağlantı yoksa ya da cihazda gönderilmeyi bekleyen kayıt varsa. Sahadaki
 * kullanıcı doldurduğunun kaybolmadığını, ne zaman gideceğini buradan görür.
 */
export function CevrimdisiRozeti() {
  const qc = useQueryClient()
  const cevrimici = useCevrimici()
  const { bekleyen, gonderiliyor, reddedilen } = useKuyrukDurumu()
  const [cakismaAcik, setCakismaAcik] = useState(false)
  if (cevrimici && bekleyen === 0 && reddedilen === 0) return null

  return (
    <div
      className={cn(
        "fixed bottom-3 left-3 z-[60] flex max-w-[calc(100vw-1.5rem)] items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-lg",
        cevrimici ? "border-sky-200 bg-sky-50 text-sky-900" : "border-amber-300 bg-amber-50 text-amber-900",
      )}
      role="status"
    >
      {gonderiliyor ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> : !cevrimici ? <CloudOff className="h-3.5 w-3.5 shrink-0" /> : <RefreshCw className="h-3.5 w-3.5 shrink-0" />}
      <span className="min-w-0 truncate">
        {!cevrimici ? "Çevrimdışı" : gonderiliyor ? "Gönderiliyor…" : "Bağlantı var"}
        {bekleyen > 0 && ` · ${bekleyen} kayıt cihazda bekliyor`}
      </span>
      {reddedilen > 0 && (
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1 font-semibold text-red-700 underline-offset-2 hover:underline"
          onClick={() => setCakismaAcik(true)}
        >
          <AlertTriangle className="h-3.5 w-3.5" /> {reddedilen} işlem uygulanamadı
        </button>
      )}
      {cevrimici && bekleyen > 0 && !gonderiliyor && (
        <button type="button" className="shrink-0 font-semibold underline-offset-2 hover:underline" onClick={() => void kuyruguGonder(qc)}>
          Şimdi gönder
        </button>
      )}
      <CakismaListesi acik={cakismaAcik} onKapat={() => setCakismaAcik(false)} />
    </div>
  )
}

/**
 * ÇAKIŞMA LİSTESİ — bağlantı yokken yapılıp sunucunun REDDETTİĞİ işlemler
 * (ör. form başkası tarafından tamamlanmış, kopya numarası dolu). Kullanıcı
 * neyin uygulanmadığını ve nedenini görür; gerekiyorsa işi yeniden yapar.
 */
function CakismaListesi({ acik, onKapat }: { acik: boolean; onKapat: () => void }) {
  useKuyrukDurumu() // liste değişince yeniden çizilsin
  const liste = reddedilenListesi()
  return (
    <Dialog open={acik} onOpenChange={(v) => { if (!v) onKapat() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Uygulanamayan işlemler</DialogTitle>
          <DialogDescription>
            Bağlantı yokken yapılan bu işlemleri sunucu kabul etmedi; başka biri aynı kaydı değiştirmiş olabilir.
            Gerekiyorsa ilgili dosyada işlemi yeniden yapın.
          </DialogDescription>
        </DialogHeader>
        <ul className="max-h-80 divide-y overflow-y-auto rounded-md border">
          {liste.map((r) => (
            <li key={r.id} className="space-y-0.5 px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{r.ozet}</span>
                <a href={`/faaliyetler/${r.projectId}`} className="shrink-0 text-xs text-primary hover:underline">Dosyayı aç</a>
              </div>
              <p className="text-xs text-red-700">{r.hata}</p>
              <p className="text-[10px] text-muted-foreground">{new Date(r.zaman).toLocaleString("tr-TR")}</p>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onKapat}>Kapat</Button>
          <Button onClick={() => { void reddedilenleriTemizle(); onKapat() }}>Gördüm, listeyi temizle</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
